/**
 * @file Представление текущей задачи загрузки.
 *
 * Читает состояние из Context, выводит прогресс и доступные действия.
 * Существование панели не управляет жизненным циклом передачи.
 */
import { useLocale } from '../../i18n/useLocale'
import TransferProgress from '../transfers/TransferProgress'
import './Upload.css'
import { uploadMessages } from './messages'
import { isUploadBusy, totalUploaded, uploadPercent } from './model'
import { useUpload } from './useUpload'

export default function UploadProgress() {
    const { state, cancel, retry, dismiss } = useUpload()
    const { locale } = useLocale()
    if (!state) return null

    const text = uploadMessages[locale]

    // Доступные действия и процент вычисляются из состояния Provider.
    const busy = isUploadBusy(state)
    const percent = uploadPercent(state)

    return (
        <section className='task-item upload-task' aria-label={state.name}>
            {/* Название и фаза описывают задачу; алгоритм запускается из Provider. */}
            <h3 className='task-title'>{state.name}</h3>

            <p className='upload-phase' role='status'>
                {text.phases[state.phase]}
            </p>

            {/* Общий счётчик получает подтверждённые байты; анимация не меняет результат передачи. */}
            <TransferProgress
                id={state.id}
                name={state.name}
                bytes={totalUploaded(state)}
                total={state.size}
                percent={percent}
                active={state.phase === 'uploading'}
            />

            {/* Ошибка передачи и предупреждение очистки имеют разный смысл и показаны отдельно. */}
            {state.error && (
                <p className='upload-error' role='alert'>
                    {text.errors[state.error]}
                </p>
            )}
            {state.phase === 'failed' && !state.retryable && (
                <p className='upload-note'>{text.unconfirmed}</p>
            )}
            {state.cleanupWarning && (
                <p className='upload-note'>{text.cleanupWarning}</p>
            )}

            {/* Действия зависят от фазы: во время завершения операции отмена уже недоступна. */}
            <div className='upload-actions'>
                {['checking', 'uploading'].includes(state.phase) && (
                    <button type='button' onClick={cancel}>
                        {text.cancel}
                    </button>
                )}
                {!busy && state.retryable && (
                    <button type='button' onClick={retry}>
                        {text.retry}
                    </button>
                )}
                {!busy && (
                    <button type='button' onClick={dismiss}>
                        {text.dismiss}
                    </button>
                )}
            </div>

            {/* Технические подробности скрыты по умолчанию, чтобы панель оставалась компактной. */}
            <details className='upload-segments'>
                <summary>{text.details}</summary>

                <p className='upload-destination'>
                    {text.storage}: {state.boxId}
                </p>

                <p className='upload-destination'>
                    {text.destination}: {state.folder}
                </p>

                <ol>
                    {state.segments.map((segment) => {
                        const length = segment.end - segment.start
                        const value =
                            length === 0
                                ? segment.status === 'done'
                                    ? 100
                                    : 0
                                : Math.floor((segment.uploaded / length) * 100)

                        return (
                            <li
                                key={segment.index}
                                className={
                                    segment.status === 'failed'
                                        ? 'upload-segment-failed'
                                        : undefined
                                }
                            >
                                <div>
                                    <span>
                                        {text.part} {segment.index + 1}
                                    </span>
                                    <span>
                                        {text.segments[segment.status]} ·{' '}
                                        {value}%
                                    </span>
                                </div>

                                <progress
                                    max={100}
                                    value={value}
                                    aria-label={`${text.part} ${segment.index + 1}`}
                                />
                            </li>
                        )
                    })}
                </ol>
            </details>
        </section>
    )
}
